import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../hooks/useEnrichmentState';
import { userAppContext } from '../../context/Context';

import Alert from '../../global/Alert/Alert';
import Loader from '../../components/Common/Loader';
import EnrichmentModal from './components/EnrichmentModal';
import ValidationMessage from './components/ValidationMessage';
import SharedModal from '../../components/Modal/SharedModal';
import { getItemCount } from '../../components/AdvancedSearch/AdvancedSearchHelpers';
import { UserContext } from '../../types';
import { advancedExamples } from '../../components/AdvancedSearch/AdvancedExamples';
import useFetchSuggestions from '../../hooks/useFetchSuggestions';
import EnrichmentSwarmPlot from './components/EnrichmentGraph';
import GseaModal from './components/GseaModal';
import GseaResultsSection, { GseaComplementBanner } from './components/GseaResultsSection';
import ComparisonView from './components/ComparisonView.tsx';
import EnrichmentResultsTabs from './components/EnrichmentResultsTabs';
import EnrichmentRunDetails from './components/EnrichmentRunDetails';
import { tabs, getAnalysisMethodOptions, isDseaEnabled, defaultGseaParams, GSEA_MAX_GENES } from './helpers';
import { buildComparisonRows } from './gseaHelpers';
import { useEnrichmentValidation, useEnrichmentAPI, useEnrichmentDownload, useGseaAPI } from './hooks';
import { AnalysisMethod, EnrichmentResult, GseaParams, ResultsTab } from './types';
import SharedButton from '@/global/Button/Button';
import { ArrowDownTrayIcon, ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import SharedBadge from '@/global/Badge/Badge';
import HomeStats from '@/components/Home/HomeStats';
import TabSelector from '@/global/TabSelector/TabSelector';
import BackgroundPages from '@/global/Background/BackgroundPages';
import RestrictedAccess from '@/components/Modal/RestrictedAccess';
import { trackEvent } from '@/utils/analytics';
import { downloadZip } from '@/utils/downloadFile';

const ENRICHMENT_ALERT_DURATION_MS = 12_000;

type EnrichmentViewMode = 'input' | 'results';

const Enrichment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enrichmentFrom, clearEnrichmentFrom } = useEnrichmentState();
  const { user, authorization, isAuthenticated } = userAppContext() as UserContext;

  const enrichmentIcon = '/static/images/icons/iconsPink/enrichment-icon.svg';

  const initialData = location.state || {};

  const [searchType, setSearchType] = useState(initialData.searchType || 'GENES');
  const [text, setText] = useState(initialData.text || '');
  const [geneFilterType, setGeneFilterType] = useState(
    initialData.selectedFilter || initialData.geneFilterType || 'geneSymbol',
  );

  const [showModal, setShowModal] = useState(false);
  const [showGseaModal, setShowGseaModal] = useState(false);
  const [trialLimitDismissed, setTrialLimitDismissed] = useState(false);
  const [analysisMethod, setAnalysisMethod] = useState<AnalysisMethod>('FISHER');
  const [isAnalysisMethodOpen, setIsAnalysisMethodOpen] = useState(true);
  const availableAnalysisMethods = getAnalysisMethodOptions();
  const selectedAnalysisMethod = availableAnalysisMethods.find(option => option.value === analysisMethod);
  const [resultsTab, setResultsTab] = useState<ResultsTab>('fisher');
  const [runGseaAfterFisher, setRunGseaAfterFisher] = useState(false);
  const [viewMode, setViewMode] = useState<EnrichmentViewMode>('input');

  const searchTypeRef = useRef(searchType);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const userGroups = user?.['cognito:groups'];
  const isAcademic = Array.isArray(userGroups) ? userGroups.includes('ROLE_ACADEMIC') : false;
  const isTrial = user?.['cognito:groups']?.[0] === 'ROLE_TRIAL';

  const [enrichmentParams, setEnrichmentParams] = useState({
    commonVariants: 2,
    commonGenes: 2,
    maxPvalue: 0.05,
    maxScore: 1,
    minScore: 0,
    pageNumber: 0,
    source: isAcademic ? 'CURATED' : 'ALL',
  });

  const [gseaParams, setGseaParams] = useState<GseaParams>({
    ...defaultGseaParams,
    source: isAcademic ? 'CURATED' : 'ALL',
  });

  // Custom hooks
  const { validation, showValidation, validateInput, clearValidation } = useEnrichmentValidation({
    text,
    searchType,
    geneFilterType,
  });

  const {
    enrichmentLoading,
    enrichmentResults,
    showErrorAlert,
    showEmptyResultsAlert,
    showSuccessAlert,
    showApiKeyAlert,
    showTrialLimitModal,
    performEnrichment,
    clearResults,
    dismissAlerts,
    setShowApiKeyAlert,
    setShowTrialLimitModal,
  } = useEnrichmentAPI({
    authorization: authorization as string,
    isAuthenticated,
    enrichmentParams,
  });

  const { csvData, setCsvData, metadataContent, setMetadataContent, swarmRef, downloadGraphPNG } =
    useEnrichmentDownload();

  const {
    gseaLoading,
    gseaResults,
    gseaCsvData,
    gseaMetadataContent,
    showGseaErrorAlert,
    showGseaEmptyResultsAlert,
    showGseaSuccessAlert,
    showGseaApiKeyAlert,
    showGseaTrialLimitModal,
    performGsea,
    clearGseaResults,
    dismissGseaAlerts,
    setShowGseaApiKeyAlert,
    setShowGseaTrialLimitModal,
  } = useGseaAPI({
    authorization: authorization as string,
    isAuthenticated,
    gseaParams,
  });

  const { notFoundItems, fetchSuggestions } = useFetchSuggestions(
    searchType,
    searchTypeRef.current === 'GENES' ? geneFilterType : 'strID',
    authorization,
    isAuthenticated,
  );

  useEffect(() => {
    if (initialData.selectedFilter) {
      setGeneFilterType(initialData.selectedFilter);
    }
  }, [initialData.selectedFilter]);

  useEffect(() => {
    if (!isDseaEnabled && analysisMethod !== 'FISHER') {
      setAnalysisMethod('FISHER');
    }
  }, [analysisMethod]);

  useEffect(() => {
    searchTypeRef.current = searchType;
    if (
      searchTypeRef.current === 'GENES' &&
      !(enrichmentFrom?.from === 'advancedSearchSelectedItems' && enrichmentFrom?.selectedFilter === 'name')
    ) {
      setGeneFilterType('geneSymbol');
    }
  }, [searchType, enrichmentFrom]);

  useEffect(() => {
    if (enrichmentResults && swarmRef.current && resultsTab === 'fisher') {
      setTimeout(() => {
        swarmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [enrichmentResults, resultsTab]);

  useEffect(() => {
    if (viewMode !== 'results') return;
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [viewMode]);

  const usesGseaInputLimit = isDseaEnabled && searchType === 'GENES' && analysisMethod !== 'FISHER';
  const maxInputItems = usesGseaInputLimit ? GSEA_MAX_GENES : 4000;

  useEffect(() => {
    if (!isTrial) return;
    const inputs = text
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    if (inputs.length > 10 && !showTrialLimitModal && !trialLimitDismissed) {
      setShowTrialLimitModal(true);
    } else if (inputs.length <= 10) {
      setShowTrialLimitModal(false);
      setTrialLimitDismissed(false);
    }
  }, [text, isTrial, showTrialLimitModal, trialLimitDismissed]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const lines = value.split('\n');

    if (lines.length > maxInputItems) {
      const limitedValue = lines.slice(0, maxInputItems).join('\n');
      setText(limitedValue);
    } else {
      setText(value);
    }
  };

  const clearInputText = () => {
    setText('');
    clearValidation();
    clearResults();
    clearGseaResults();
    setResultsTab('fisher');
    setRunGseaAfterFisher(false);
    setViewMode('input');
  };

  const handleBackToSetup = () => {
    setViewMode('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showExamples = () => {
    const formatFilterType =
      searchTypeRef.current === 'GENES'
        ? geneFilterType.replace('Id', 'ID').replace('geneSymbol', 'symbol')
        : 'strID';

    const exampleItems = advancedExamples[searchTypeRef.current]?.[formatFilterType] || [];
    setText(exampleItems.join('\n'));
  };

  const handleGeneFilterTypeChange = (newFilterType: string) => {
    setGeneFilterType(newFilterType);
  };

  const handleAutoDetectSuggestion = () => {
    if (validation?.detectedType && searchType === 'GENES') {
      handleGeneFilterTypeChange(validation.detectedType);
    }
  };

  const handleSearchTypeChange = (newSearchType: string) => {
    setSearchType(newSearchType);
    if (newSearchType === 'VARIANTS') {
      setAnalysisMethod('FISHER');
    }

    if (newSearchType === 'VARIANTS' && text.trim()) {
      validateInput();
    }
  };

  const handleModalSubmit = async (params: any) => {
    setEnrichmentParams(params);
    setShowModal(false);
    setCsvData(null);
    setMetadataContent(null);
    setResultsTab('fisher');

    const result = await performEnrichment(searchType, text, geneFilterType, params);
    if (result) {
      setCsvData(result.csvData);
      setMetadataContent(result.metadataData);
    }

    if (isDseaEnabled && (analysisMethod === 'BOTH' || runGseaAfterFisher)) {
      setGseaParams(prev => ({
        ...prev,
        source: params.source,
        minScore: params.minScore,
        maxScore: params.maxScore,
      }));
      setRunGseaAfterFisher(false);
      setViewMode('results');
      setShowGseaModal(true);
      return;
    }

    setViewMode('results');
  };

  const handleGseaModalSubmit = async (params: GseaParams) => {
    setGseaParams(params);
    setShowGseaModal(false);

    const result = await performGsea(text, geneFilterType, params);
    if (result) {
      setResultsTab(enrichmentResults ? 'comparison' : 'gsea');
    }

    setViewMode('results');
  };

  const openComplementGsea = () => {
    setRunGseaAfterFisher(false);
    setGseaParams(prev => ({
      ...prev,
      source: enrichmentParams.source,
      minScore: enrichmentParams.minScore,
      maxScore: enrichmentParams.maxScore,
    }));
    setShowGseaModal(true);
  };

  const goBack = () => {
    if (enrichmentFrom?.from === 'advancedSearch' || enrichmentFrom?.from === 'advancedSearchSelectedItems') {
      const { searchType, selectedFilter, text } = enrichmentFrom;

      navigate('/advancedSearch', {
        state: { searchType, selectedFilter, text },
      });
    } else {
      navigate('/');
    }

    clearEnrichmentFrom();
  };

  const handleEnrichmentClick = async () => {
    trackEvent({
      name: 'enrichment_run',
      buttonId: 'enrichment_run',
      label: 'Run enrichment',
      page: 'enrichment',
      section: 'main_actions',
      feature: 'enrichment',
      eventCategory: 'enrichment',
    });
    if (validation && !validation.isValid) {
      alert('Please fix the validation errors before running enrichment');
      return;
    }

    const inputs = text
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    await fetchSuggestions(inputs);

    if (isDseaEnabled && searchType === 'GENES' && analysisMethod === 'GSEA') {
      setShowGseaModal(true);
      return;
    }

    if (isDseaEnabled && searchType === 'GENES' && analysisMethod === 'BOTH') {
      setRunGseaAfterFisher(true);
    }

    setShowModal(true);
  };

  const handleDownloadResults = async () => {
    trackEvent({
      name: 'download_result',
      buttonId: 'enrichment_download_results_zip',
      label: 'Download enrichment results zip',
      page: 'enrichment',
      section: 'results_actions',
      feature: 'download',
      eventCategory: 'enrichment',
    });
    if (!csvData && !gseaCsvData) {
      alert('No data available to download');
      return;
    }

    try {
      const files: { name: string; content: string }[] = [];

      if (csvData && metadataContent) {
        const csvForDownload = (() => {
          if (!enrichmentResults || enrichmentResults.length === 0) return csvData;

          const escapeCsvTailValue = (value: unknown, delimiter: string): string => {
            if (value === null || value === undefined) return '';
            const str = String(value);
            if (new RegExp(`[${delimiter}"\\n\\r]`).test(str)) return `"${str.replace(/"/g, '""')}"`;
            return str;
          };

          const splitCsvRecordsPreservingQuotes = (csvText: string): string[] => {
            const records: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < csvText.length; i += 1) {
              const ch = csvText[i];

              if (ch === '"') {
                if (inQuotes && csvText[i + 1] === '"') {
                  current += '""';
                  i += 1;
                } else {
                  inQuotes = !inQuotes;
                  current += ch;
                }
                continue;
              }

              if (ch === '\n' && !inQuotes) {
                records.push(current.replace(/\r$/, ''));
                current = '';
                continue;
              }

              current += ch;
            }

            if (current.length > 0) records.push(current.replace(/\r$/, ''));
            return records;
          };

          const records = splitCsvRecordsPreservingQuotes(csvData);
          if (records.length < 2) return csvData;

          const headerLine = records[0];
          if (headerLine.includes('oddsRatio') && headerLine.includes('score')) return csvData;

          const splitCsvLine = (line: string, delimiterChar: string): string[] => {
            const fields: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i += 1) {
              const ch = line[i];

              if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') {
                  current += '""';
                  i += 1;
                } else {
                  inQuotes = !inQuotes;
                }
                continue;
              }

              if (ch === delimiterChar && !inQuotes) {
                fields.push(current);
                current = '';
                continue;
              }

              current += ch;
            }

            fields.push(current);
            return fields;
          };

          const candidateDelimiters = [',', ';', '\t'];
          const firstDataLine = records.find((r, i) => i > 0 && r.trim().length > 0) ?? records[1];

          const best = candidateDelimiters
            .map((delim, index) => {
              const headerFields = splitCsvLine(headerLine, delim);
              const dataFields = splitCsvLine(firstDataLine, delim);
              const ok = headerFields.length === dataFields.length;
              return { delim, ok, count: headerFields.length, index };
            })
            .filter(x => x.ok)
            .sort((a, b) => {
              const byCount = b.count - a.count;
              return byCount !== 0 ? byCount : a.index - b.index;
            })[0];

          const delimiter = best?.delim ?? ',';
          const nextHeaderLine = `${headerLine}${delimiter}oddsRatio${delimiter}score`;

          const rows = records.slice(1).map((row, idx) => {
            const item = enrichmentResults[idx] as EnrichmentResult | undefined;
            const oddsRatioStr = escapeCsvTailValue(item?.oddsRatio, delimiter);
            const scoreStr = escapeCsvTailValue(item?.score, delimiter);
            return `${row}${delimiter}${oddsRatioStr}${delimiter}${scoreStr}`;
          });

          return [nextHeaderLine, ...rows].join('\n');
        })();

        files.push({ name: 'enrichment_results.csv', content: csvForDownload });
        files.push({ name: 'enrichment_metadata.txt', content: metadataContent });
      }

      if (gseaCsvData && gseaMetadataContent) {
        files.push({ name: 'gsea_results.csv', content: gseaCsvData });
        files.push({ name: 'gsea_metadata.txt', content: gseaMetadataContent });
      }

      await downloadZip({
        zipName: `enrichment_${searchType.toLowerCase()}`,
        files,
      });
    } catch (error) {
      console.error('Error downloading results:', error);
      alert('Error downloading results. Please try again.');
    }
  };

  const handleDownloadGraph = async () => {
    trackEvent({
      name: 'download_result',
      buttonId: 'enrichment_download_graph_png',
      label: 'Download enrichment graph png',
      page: 'enrichment',
      section: 'graph',
      feature: 'download',
      eventCategory: 'enrichment',
    });
    try {
      await downloadGraphPNG();
    } catch (error) {
      console.error('Error downloading graph:', error);
      alert('Error downloading graph. Please try again.');
    }
  };

  const canRunEnrichment = text.trim() && (!validation || validation.isValid);
  const comparisonRows = buildComparisonRows(enrichmentResults, gseaResults);
  const hasFisherResults = Boolean(enrichmentResults?.length);
  const hasGseaResults = isDseaEnabled && Boolean(gseaResults?.length);
  const showResultsTabs = hasFisherResults || hasGseaResults;
  const showGseaComplementBanner =
    isDseaEnabled &&
    searchType === 'GENES' &&
    hasFisherResults &&
    !hasGseaResults &&
    analysisMethod === 'FISHER';
  const lockDseaSharedParams = analysisMethod === 'BOTH' || (hasFisherResults && analysisMethod === 'FISHER');
  const runButtonLabel =
    isDseaEnabled && searchType === 'GENES' && analysisMethod === 'GSEA'
      ? 'Run DSEA'
      : isDseaEnabled && searchType === 'GENES' && analysisMethod === 'BOTH'
        ? 'Run Both Analyses'
        : 'Run Enrichment';
  const isLoading = enrichmentLoading || (isDseaEnabled && gseaLoading);

  const analysisSummaryLabel =
    isDseaEnabled && searchType === 'GENES' && analysisMethod === 'GSEA'
      ? 'DSEA'
      : isDseaEnabled && searchType === 'GENES' && analysisMethod === 'BOTH'
        ? 'ORA + DSEA'
        : searchType === 'VARIANTS'
          ? 'Variant enrichment'
          : 'ORA enrichment';

  const getAvailableGeneFilterOptions = () => {
    const baseOptions = [
      { value: 'geneSymbol', label: 'Gene symbol' },
      { value: 'ensemblId', label: 'Ensembl ID' },
      { value: 'uniprotId', label: 'UniProt ID' },
      { value: 'geneId', label: 'NCBI Gene ID' },
    ];

    if (enrichmentFrom?.from === 'advancedSearchSelectedItems' && enrichmentFrom?.selectedFilter === 'name') {
      return [{ value: 'name', label: 'Gene name' }, ...baseOptions];
    }

    return baseOptions;
  };

  const availableGeneFilterOptions = getAvailableGeneFilterOptions();

  return (
    <div className="apply-trial-page">
      <BackgroundPages />
      <div className="relative z-10">
        <div className="container mx-auto md:px-4 py-8">
          {isLoading && <Loader style={{ position: 'fixed' }} />}

          <div className="mb-4 text-left">
            <SharedBadge
              text="Disease Enrichment Tool"
              icon={<img src={enrichmentIcon} alt="Enrichment" className="h-4 w-4 mr-1" />}
              variant="secondary"
              size="sm"
            />
            {viewMode === 'input' ? (
              <>
                <h1 className="text-5xl font-semibold my-3 ">Disease Enrichment Analysis</h1>
                <p className="mb-4 text-lg font-medium text-blue w-3/4">
                  Perform enrichment analysis on your gene or variant lists to identify significant
                  associations with diseases and phenotypes from the largest curated collection available.
                </p>
              </>
            ) : (
              <h1 className="text-3xl md:text-4xl font-semibold my-3">Enrichment Results</h1>
            )}
          </div>

          <RestrictedAccess isRestricted={!isAuthenticated}>
            {viewMode === 'input' && (
              <div className="bg-white p-4 border border-gray-200 rounded-2xl">
                {/* Search Type Selection */}
                <div className="mb-6" data-demo-tour="enrichment-search-type-tabs">
                  <TabSelector
                    options={tabs.map(type => ({ label: type.toUpperCase(), value: type }))}
                    variant="primary"
                    size="sm"
                    onChange={handleSearchTypeChange}
                  />
                </div>

                {/* Gene/Variant identifier dropdown */}
                {searchType === 'GENES' ? (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Identifier type</label>
                      <select
                        value={geneFilterType}
                        onChange={e => handleGeneFilterTypeChange(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                      >
                        {availableGeneFilterOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {geneFilterType === 'name' ? (
                        <p className="text-xs mt-1">
                          Gene names from Advanced Search. Changing this filter will clear the current list.
                        </p>
                      ) : null}
                    </div>

                    {availableAnalysisMethods.length > 1 && (
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={() => setIsAnalysisMethodOpen(open => !open)}
                          className="flex w-full items-center justify-between gap-2 text-left"
                          aria-expanded={isAnalysisMethodOpen}
                          data-demo-tour="enrichment-analysis-method-toggle"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            Analysis method
                            {!isAnalysisMethodOpen && selectedAnalysisMethod ? (
                              <span className="ml-2 font-normal text-gray-500">
                                · {selectedAnalysisMethod.label}
                              </span>
                            ) : null}
                          </span>
                          <ChevronDownIcon
                            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                              isAnalysisMethodOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {isAnalysisMethodOpen && (
                          <div className="mt-2 space-y-2">
                            {availableAnalysisMethods.map(option => (
                              <label
                                key={option.value}
                                className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer ${
                                  analysisMethod === option.value
                                    ? 'border-blue-400 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="analysisMethod"
                                  value={option.value}
                                  checked={analysisMethod === option.value}
                                  onChange={() => setAnalysisMethod(option.value)}
                                  className="mt-1"
                                />
                                <span>
                                  <span className="block text-sm font-semibold text-gray-900">
                                    {option.label}
                                  </span>
                                  <span className="block text-xs text-gray-600 mt-1">
                                    {option.description}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Variant identifier</label>
                    <input
                      readOnly
                      value="rs number"
                      className="w-full p-2 border border-gray-300 bg-gray-50 rounded-2xl"
                      disabled
                    />
                  </div>
                )}

                {/* Text Input Area */}
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 ">
                    {geneFilterType === 'name' ? (
                      'Selected gene names from Advanced Search:'
                    ) : (
                      <p className="ml-2">
                        Add your search list{' '}
                        <span className="font-thin text-xs">(separate items by line)</span>:
                      </p>
                    )}
                  </label>
                  <textarea
                    placeholder={
                      geneFilterType === 'name'
                        ? 'Gene names from Advanced Search (read-only)'
                        : `Enter ${searchType.toLowerCase()} (one per line)`
                    }
                    value={text}
                    onChange={handleQueryChange}
                    readOnly={geneFilterType === 'name'}
                    className={`w-full p-3 border rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 ${
                      geneFilterType === 'name'
                        ? 'bg-gray-50 cursor-not-allowed'
                        : validation && !validation.isValid
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                    }`}
                    rows={8}
                    data-demo-tour="enrichment-input-textarea"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-sm text-gray-500">Items: {getItemCount(text)}</p>
                    {searchType === 'GENES' && usesGseaInputLimit && (
                      <p className="text-xs text-gray-500">DSEA supports up to {GSEA_MAX_GENES} genes</p>
                    )}
                  </div>
                </div>

                {/* Validation Messages */}
                {showValidation && (
                  <ValidationMessage validation={validation} onSuggestionClick={handleAutoDetectSuggestion} />
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 md:gap-0">
                  <SharedButton
                    id="enrichment_back"
                    variant="secondary"
                    onClick={goBack}
                    className="filter-upload"
                    disabled={!isAuthenticated}
                  >
                    Back
                  </SharedButton>

                  <SharedButton
                    id="enrichment_run"
                    variant="gradient"
                    onClick={handleEnrichmentClick}
                    disabled={
                      isLoading ||
                      !canRunEnrichment ||
                      (isTrial && getItemCount(text) > 10) ||
                      !isAuthenticated
                    }
                    className={`filter-upload ${
                      !canRunEnrichment || (isTrial && getItemCount(text) > 10)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-demo-tour="enrichment-run-button"
                  >
                    {runButtonLabel}
                    {isLoading && <Loader style={{ position: 'fixed' }} />}
                  </SharedButton>

                  <SharedButton
                    id="enrichment_clear"
                    variant="secondary"
                    onClick={clearInputText}
                    disabled={!isAuthenticated}
                    className={`filter-upload ${
                      !canRunEnrichment || (isTrial && getItemCount(text) > 10)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    Clear List
                  </SharedButton>

                  <SharedButton
                    id="enrichment_examples"
                    variant="gradient"
                    onClick={showExamples}
                    className="filter-upload"
                    disabled={!isAuthenticated}
                    data-demo-tour="enrichment-show-examples"
                  >
                    Show Examples
                  </SharedButton>
                </div>
              </div>
            )}
          </RestrictedAccess>

          {/* Enrichment Modal */}
          {showModal && (
            <EnrichmentModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={handleModalSubmit}
              initialParams={enrichmentParams}
              searchType={searchType}
              notFoundItems={notFoundItems}
              user={user}
              currentText={text}
            />
          )}

          {isDseaEnabled && showGseaModal && (
            <GseaModal
              isOpen={showGseaModal}
              onClose={() => setShowGseaModal(false)}
              onSubmit={handleGseaModalSubmit}
              initialParams={gseaParams}
              user={user}
              lockSharedParams={lockDseaSharedParams}
              inheritedSource={enrichmentParams.source}
              inheritedMinScore={enrichmentParams.minScore}
              inheritedMaxScore={enrichmentParams.maxScore}
            />
          )}

          {/* Results view */}
          {viewMode === 'results' && (
            <div ref={resultsRef} className="mt-6" data-demo-tour="enrichment-results-area">
              <div
                className="mb-6 p-4 bg-white border border-gray-200 rounded-2xl"
                data-demo-tour="enrichment-results-summary"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {analysisSummaryLabel}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getItemCount(text)} {searchType === 'GENES' ? 'genes' : 'variants'} analyzed
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <SharedButton
                      id="enrichment_back_to_setup"
                      variant="secondary"
                      onClick={handleBackToSetup}
                      className="filter-upload"
                    >
                      <ArrowLeftIcon className="h-4 w-4 mr-1" />
                      New enrichment
                    </SharedButton>
                    {(csvData || gseaCsvData) && (
                      <SharedButton
                        id="enrichment_download"
                        variant="secondaryOutline"
                        onClick={handleDownloadResults}
                        className="filter-upload"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download Results (ZIP)
                      </SharedButton>
                    )}
                  </div>
                </div>
                <EnrichmentRunDetails
                  analysisMethod={analysisMethod}
                  searchType={searchType}
                  geneFilterType={geneFilterType}
                  itemCount={getItemCount(text)}
                  validation={validation}
                  notFoundItems={notFoundItems}
                  enrichmentParams={enrichmentParams}
                  gseaParams={gseaParams}
                  showOra={hasFisherResults || analysisMethod === 'FISHER' || analysisMethod === 'BOTH'}
                  showDsea={
                    isDseaEnabled &&
                    searchType === 'GENES' &&
                    (hasGseaResults || analysisMethod === 'GSEA' || analysisMethod === 'BOTH')
                  }
                />
              </div>

              {showErrorAlert && (
                <Alert
                  message={`There was an error running the ${searchType.toLowerCase()} enrichment analysis.`}
                  type="error"
                  autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
                  onClose={dismissAlerts}
                />
              )}

              {showEmptyResultsAlert && (
                <Alert
                  message="No significant disease associations were found for the provided identifiers with the selected parameters."
                  type="warning"
                  autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
                  onClose={dismissAlerts}
                />
              )}

              {showSuccessAlert && (
                <Alert
                  message="Enrichment analysis completed successfully."
                  type="success"
                  autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
                  onClose={dismissAlerts}
                />
              )}

              {isDseaEnabled && showGseaErrorAlert && (
                <Alert
                  message="There was an error running the DSEA analysis."
                  type="error"
                  autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
                  onClose={dismissGseaAlerts}
                />
              )}

              {isDseaEnabled && showGseaEmptyResultsAlert && (
                <Alert
                  message="No significant DSEA associations were found for the provided genes with the selected parameters."
                  type="warning"
                  autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
                  onClose={dismissGseaAlerts}
                />
              )}

              {isDseaEnabled && showGseaSuccessAlert && (
                <Alert
                  message="DSEA analysis completed successfully."
                  type="success"
                  autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
                  onClose={dismissGseaAlerts}
                />
              )}

              {showResultsTabs && (
                <EnrichmentResultsTabs
                  activeTab={resultsTab}
                  onChange={setResultsTab}
                  showFisher={hasFisherResults}
                  showGsea={hasGseaResults}
                  showComparison={hasFisherResults && hasGseaResults}
                />
              )}

              {showGseaComplementBanner && resultsTab === 'fisher' && (
                <GseaComplementBanner onRunGsea={openComplementGsea} disabled={gseaLoading} />
              )}

              <div>
                {hasFisherResults && hasGseaResults && resultsTab === 'comparison' && (
                  <ComparisonView rows={comparisonRows} />
                )}

                {hasFisherResults && resultsTab === 'fisher' && (
                  <EnrichmentSwarmPlot
                    ref={swarmRef}
                    data={enrichmentResults!}
                    onDownload={handleDownloadGraph}
                    geneFilterType={searchType === 'GENES' ? geneFilterType : undefined}
                  />
                )}

                {hasGseaResults && resultsTab === 'gsea' && (
                  <GseaResultsSection data={gseaResults!} isTrial={isTrial} />
                )}
              </div>
            </div>
          )}

          {showApiKeyAlert && (
            <Alert
              message="You need to set your API key."
              type="warning"
              autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
              onClose={() => setShowApiKeyAlert(false)}
            >
              <span
                onClick={() => navigate('/Profile-area#apiKey')}
                className="font-bold ml-2 cursor-pointer hover:text-blue-600"
              >
                Set API Key
              </span>
            </Alert>
          )}

          {isDseaEnabled && showGseaApiKeyAlert && (
            <Alert
              message="You need to set your API key."
              type="warning"
              autoHideDuration={ENRICHMENT_ALERT_DURATION_MS}
              onClose={() => setShowGseaApiKeyAlert(false)}
            >
              <span
                onClick={() => navigate('/Profile-area#apiKey')}
                className="font-bold ml-2 cursor-pointer hover:text-blue-600"
              >
                Set API Key
              </span>
            </Alert>
          )}

          {/* Trial Limit Modal */}
          <SharedModal
            open={showTrialLimitModal || (isDseaEnabled && showGseaTrialLimitModal)}
            setOpen={value => {
              setShowTrialLimitModal(value);
              setShowGseaTrialLimitModal(value);
              if (!value) {
                setTrialLimitDismissed(true);
              }
            }}
            title="Trial User Limit"
            content={
              <div className="mt-4">
                <p className="text-gray-700 mb-4">
                  Trial users can only run enrichment analysis with up to <strong>10 items</strong>, but will
                  receive results for up to <strong>30 items</strong> from the analysis.
                </p>
                <p className="text-gray-700 mb-4">
                  You currently have <strong>{getItemCount(text)} items</strong> in your list.
                </p>
                <p className="text-gray-600 mb-6">
                  To analyze larger datasets (up to 4,000 items), please upgrade your plan.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowTrialLimitModal(false);
                      setTrialLimitDismissed(true);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowTrialLimitModal(false);
                      setTrialLimitDismissed(true);
                      navigate('/plans');
                    }}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    View Plans
                  </button>
                </div>
              </div>
            }
          />
        </div>
        <section className="home-stats-wrapper mt-4">
          <HomeStats />
        </section>
      </div>
    </div>
  );
};

export default Enrichment;
